<template>
	<LAppFrame>
		<template #sidebar>
			<LBrandMark />
			
			<div style="padding:0 20px 12px; margin-top: 8px;" v-if="showGlobalSearch">
				<LGlobalSearch />
			</div>

			<LNav>
				<!-- Section menu title -->
				<template v-if="isAdminSection">
					<SideNavItem
						as="router-link"
						to="/home"
						class="nv-back"
						style="font-weight: bold; margin-bottom: 10px;"
					>
						<template #icon>
							<LIcon name="angle-left" size="12px" weight="solid" />
						</template>
						<span style="opacity: .7;">
							Back to portal
						</span>
					</SideNavItem>

					<SideNavItem
						as="span"
						class="nv-section-label"
					>
						<span style="opacity: .7;text-align: left; text-transform: uppercase;">
							Administration
						</span>
					</SideNavItem>
				</template>

				<!-- Menu items -->
				<template v-for="n in getNavItems" :key="n.id">
					<!-- Child level -->
					<LSideNavGroup :label="n.text" :item="n" :url="n.url || null" :active="!!n.url && route.path === n.url" v-if="isNavGroup(n) && isNavGroupShown(n)">
						<template v-for="c in n.children" :key="c.text">
							<LSubNavItem v-if="isNavItemVisible(c)" :active="isNavChildActive(c)" @click="active = c.text"
								as="router-link"
								:to="c.url"
							>{{ c.text }}
							<!-- [{{ route.name }} -- {{ c.url }}] -->
								<template v-if="c.badgeKey" #trailing>
									<LCountBadge :count="navBadgeCount(c)" :tone="navBadgeTone(c)" :label="navBadgeLabel(c)" :tooltip="navBadgeTooltip(c)" />
								</template>
							</LSubNavItem>
						</template>
						<template #icon>
							<LIcon :name="n.icon" :full-name="n.faIcon" />
						</template>
					<!-- Shown only while the group is collapsed: open, the badge sits on the item itself. -->
						<template v-if="n.badgeKey" #trailing="{ open }">
							<LCountBadge v-if="isGroupBadgeShown(open)" :count="navBadgeCount(n)" :tone="navBadgeTone(n)" :label="navBadgeLabel(n)" :tooltip="navBadgeTooltip(n)" />
						</template>
					</LSideNavGroup>
					
					<template v-else-if="!isNavGroup(n)">
						<!-- Top level, stand alone, wrapped in permission -->
						<LEntitlementGate v-if="n.text === 'Rostering'" :entitled="hasRostering">
							<SideNavItem :active="route.path === n.url" @click="active = n.text"
								:key="n.url"
								as="router-link"
								:to="n.url"
							>
								<template #icon>
									<LIcon :name="n.icon" :full-name="n.faIcon" />
								</template>{{ n.text }}
							</SideNavItem>
						</LEntitlementGate>

						<!-- Top level, stand alone -->
						<SideNavItem v-else :active="route.path === n.url" @click="active = n.text"
							:key="n.url"
							as="router-link"
							:to="n.url"
						>
							<template #icon>
								<LIcon :name="n.icon" :full-name="n.faIcon" />
							</template>{{ n.text }}
						</SideNavItem>
					</template>
				</template>
			</LNav>

			<UserMenu />
		</template>


		<!-- System messages from Lotus. Above everything else on the page, because a
			maintenance notice is no use discovered halfway down. This layout is mounted
			once for the whole signed-in session, so the list is fetched once, not per
			navigation. -->
		<SystemMessageBanners />

		<LBreadcrumbs :items="getBreadcrumbs" style="margin-bottom: var(--sp-2);" />

		<!-- Just for developer assistance -->
		<!-- <div>
			{{ this.$route.name }}
		</div> -->

		<LPageHeader
			:eyebrow="getEyebrow"
			:title="getPageTitle"
			:subtitle="getSubTitle"
		>
			<template #actions>
				<!-- <LButton variant="primary">
					<template #icon>
						<LIcon name="plus" :size="14" />
					</template>
					Add time
				</LButton> -->
			</template>
		</LPageHeader>
		<!-- <p style="color:var(--text-3);font-size:13px"></p>

		<hr/> -->

		<div :class="{ 'admin-section': isAdminSection }" data-section="page-content">
			<slot></slot>
		</div>

		<!-- Idle timeout and max session duration. Every signed-in page is inside
			this layout, so this is the one place they need to be. -->
		<SessionGuard />
	</LAppFrame>
</template>





<script>
import globalStore from "/globalStore.js";
import accountAdminMixin from "/account-admin/account-admin-mixin";
import config from "/config.js";
import primaryNavigationSetup from "/navigation/primary-navigation-setup.js";
import { resolvePageTitleSegments, resolvePageTitleText } from "/navigation/page-title.js";

import { useRoute } from 'vue-router'

import
{
	LAppFrame, LNav, SideNavItem, LSideNavGroup, LSubNavItem, LBrandMark,
	LGlobalSearch, LPageHeader, LButton, LEntitlementGate, LIcon,
	LBreadcrumbs, LCountBadge,
} from '/components2'

import
{
	loadApprovalsAvailabilityAsync, refreshNavCountsAsync, getNavBadgeCount, isNavKeyVisible, getApprovalsWindowWeeks,
} from "/module.timesheets/approvals/approvals-store.js";
import { resolveNavBadge } from "/module.timesheets/approvals/approvals-display-values.js";

import UserMenu from "./components/UserMenu.vue";
import SessionGuard from "./components/SessionGuard.vue";
import SystemMessageBanners from "/system-messages/components/SystemMessageBanners.vue";

export default {
	name: "PrimaryLayout",





	emits: ["child-event"],





	mixins: [accountAdminMixin],





	components: {
		LAppFrame, LNav, SideNavItem, LSideNavGroup, LSubNavItem, LBrandMark,
		LGlobalSearch, LPageHeader, LButton, LEntitlementGate, LIcon,
		LBreadcrumbs, LCountBadge,
		UserMenu, SessionGuard, SystemMessageBanners,
	},





	props: [
		'subPageTitle'
	],

	



	data() {
		return {
			globalStore: globalStore,
			config: config,

			pageTitle: "",
			pageSubTitle: "",

			portalNavItemCount: 0,
			portalNavtems: [],
			adminNavItemsCount: 0,
			adminNavItems: [],

			isMobile: false,

			isSideNavClosed: false,
			triggerClosedMenu: false,
			triggerOpenMenu: false,

			section: "",
			sectionLayoutClass: "",

			layoutType: "",	// The "layout-type" specified for the page in the route meta. "account-admin" or "" (default)

			layoutContainer: "", // specified in route meta data. Default is "card"


			//-- For "account-admin" layouts
			accessAccountId: "",

			moduleTitle: "Lotus",
			moduleSubTitle: "Portal",


			imageOnlyFullSizeUrl: config.logo.imageOnlyFullSizeUrl,
			
			route: useRoute(),

			// Now design
			showGlobalSearch: false

		};
	},



	mounted() 
	{
		this.init();
	},



	created() 
	{
		window.addEventListener("resize", this.onWindowResize);

		this.init();
	},



	// This is to derive the pageTitle from route, which will change.
	// This works, but using computed instead.
    // watch: {
	// 	$route(to, from) {
	// 		this.pageTitle = to.meta.pageTitle;
	// 	}
	// },

	watch: {
		// The nav badges follow the user around: re-read on every route change. The store throttles
		// this to once a minute, and an action that changes a count forces its own refresh.
		"route.fullPath"()
		{
			this.refreshApprovalsNav();
		},
	},



	computed: {
		// The header title, resolved from `meta.pageTitle` (title path parts separated by
		// ' > ', `{placeholder}` tokens filled in by the page — see /navigation/page-title.js)
		// and rendered as the path, joined with a chevron.
		getPageTitle() {
			var _this = this;

			var pathParts = resolvePageTitleSegments(this.$route);

			var finalTitle = "";
			pathParts.forEach((part) => {
				if (finalTitle != "")
				{
					finalTitle = finalTitle + ' ' + _this.getPathSeparator() + ' ';
				}
				// LPageHeader renders the title with v-html (for the chevron), so parts
				// carrying page data (a client name, a report name) must be escaped.
				finalTitle = finalTitle + _this.escapeHtml(part);
			});

			return finalTitle;
		},



		// Breadcrumb trail for the current page, built by walking `meta.parentRouteName`
		// up from the current route. Reactive on $route, so it rebuilds on every navigation.
		// LBreadcrumbs itself renders nothing for trails shallower than 3 levels.
		getBreadcrumbs()
		{
			const crumbs = [
				{ label: this.getCurrentBreadcrumbLabel() }	// Current page: label only, no link.
			];

			let parentName = this.$route.meta.parentRouteName;
			let depth = 0;

			while (parentName && depth < 10)	// depth guard against an accidental parent cycle
			{
				depth++;

				const parentRoute = this.$router.getRoutes().find((r) => r.name === parentName);
				if (!parentRoute) break;

				let to = null;
				try
				{
					// Re-use the current route's params (a child path normally contains its parent's params).
					to = this.$router.resolve({ name: parentName, params: this.$route.params }).fullPath;
				}
				catch (e)
				{
					// The parent needs a param the current route doesn't have — show the crumb without a link.
				}

				crumbs.unshift({ label: this.getBreadcrumbLabel(parentRoute.meta), to: to });

				parentName = parentRoute.meta.parentRouteName;
			}

			// Start the trail with the module name (unlinked) when the route declares one.
			const moduleCode = this.$route.meta.module ? this.$route.meta.module : "";
			if (moduleCode != "")
			{
				const moduleName = this.getModuleDisplayName(moduleCode);

				// Don't double up when the trail already starts with the module name (e.g. on the module's root page).
				if (crumbs[0].label != moduleName)
				{
					crumbs.unshift({ label: moduleName });
				}
			}

			return crumbs;
		},



		getEyebrow()
		{
			//TODO: Implement
			return "";
		},



		getSubTitle()
		{
			// A route's one-line description under the page title (`meta.pageSubtitle`).
			return this.$route.meta.pageSubtitle ? this.$route.meta.pageSubtitle : "";
		},


		
		getPath()
		{
			return this.route.path;
		},



		isAdminSection()
		{
			return this.getPath.startsWith("/admin");
		},




		getNavItems()
		{
			if (this.isAdminSection) return this.adminNavItems;
			return this.portalNavItems;
		}
	},



	methods: {
		init() 
		{
			this.pageTitle = this.$route.meta.pageTitle;

			this.layoutType = this.$route.meta.layoutType;
			if (!this.layoutType) this.layoutType = "default";

			this.layoutContainer = this.$route.meta.layoutContainer;

			if (!this.layoutContainer) this.layoutContainer = "card";
			
			// Check the user is [still] logged in.
			this.$root.checkCurrentAuthTokenStillValid();

			this.$parent.$emit('child-event', 'primary layout emit');


			this.authoriseAccessIfSystemAdmin();

			// Make sure side menu is closed when loading on mobile.
			this.onWindowResize();


			// Module title
			const route = this.$route;
			let module = route.meta.module ? route.meta.module : "";
			
			//TODO: This is quick and dirty. We need a better module level config for things like this.
			if (module == "tesstaClients")
			{
				// "Tessta Client Portal"
				this.moduleTitle = "Tessta";
				this.moduleSubTitle = "Client Portal";
			}
			else
			{
				// Everything else ("Lotus Portal")
				this.moduleTitle = "Lotus";
				this.moduleSubTitle = "Portal";
			}

			this.initPortalMenu();
			this.initAdminMenu();

			this.initForAccountAdmin();

			this.initApprovalsNav();
		},



		/**
		 * Load whether the Approvals workspace exists for this user (it decides whether the nav item
		 * shows), then the badge counts.
		 */
		async initApprovalsNav()
		{
			await loadApprovalsAvailabilityAsync();
			await refreshNavCountsAsync();
		},



		async refreshApprovalsNav()
		{
			// Availability first, so an account whose approval was just switched on gets the nav item
			// without a full reload (it is re-read at most every five minutes).
			await loadApprovalsAvailabilityAsync();
			await refreshNavCountsAsync();
		},



		/**
		 * Whether a nav item shows. An item with a `visibleWhen` key is hidden until the store says so
		 * (the Approvals item, until approval is on and the user is an approver).
		 */
		isNavItemVisible(item)
		{
			return isNavKeyVisible(item.visibleWhen);
		},



		/**
		 * A navigation entry with children is a group.
		 */
		isNavGroup(item)
		{
			return !!item.children && item.children.length > 0;
		},



		/**
		 * A group shows when at least one of its children does. An approver without Timesheets.Core holds a
		 * group whose only child is Approvals, which stays hidden until approval is on for the account; the
		 * group must not sit there empty meanwhile.
		 */
		isNavGroupShown(group)
		{
			return group.children.some((child) => this.isNavItemVisible(child));
		},



		/**
		 * Whether a sub-nav item is the active one. Normally an exact path match; an item that sets
		 * `matchByRouteNames` (Approvals, which has bulk / outstanding / day sub-routes) is active on
		 * any of its `associatedRouteNames`.
		 */
		isNavChildActive(item)
		{
			if (item.matchByRouteNames) return (item.associatedRouteNames || []).includes(this.route.name);

			return this.route.path === item.url;
		},



		navBadgeCount(item)
		{
			return getNavBadgeCount(item.badgeKey);
		},



		navBadgeTone(item)
		{
			return resolveNavBadge(item.badgeKey).Tone;
		},



		navBadgeLabel(item)
		{
			return resolveNavBadge(item.badgeKey).Describe(this.navBadgeCount(item), getApprovalsWindowWeeks());
		},



		navBadgeTooltip(item)
		{
			return resolveNavBadge(item.badgeKey).Explain(this.navBadgeCount(item), getApprovalsWindowWeeks());
		},



		/**
		 * The group header's badge shows only while the group is collapsed.
		 */
		isGroupBadgeShown(isOpen)
		{
			return isOpen === false;
		},



		initPortalMenu()
		{
			const params = {
				currentRouteName: this.$root.getCurrentRouteName(),
				isSystemAdminUser: this.$root.isSystemAdminUser(),
				context: "standard",
				currentRoute: this.$route,
			};

			const mainMenu = primaryNavigationSetup.initialiseMainMenu(params);

			console.log("initMainMenu(): mainMenu=", mainMenu);

			this.portalNavItemCount = mainMenu.itemCount;
			this.portalNavItems = mainMenu.items;
			this.helpUrl = mainMenu.helpUrl;

			this.isLoadingMenu = false;
		},



		initAdminMenu()
		{
			const params = {
				currentRouteName: this.$root.getCurrentRouteName(),
				isSystemAdminUser: this.$root.isSystemAdminUser(),
				context: "standard",
				currentRoute: this.$route,
			};

			const adminMenu = primaryNavigationSetup.initialiseAdminMenu(params);

			this.adminNavItemsCount = adminMenu.itemCount;
			this.adminNavItems = adminMenu.items;

			this.isLoadingMenu = false;
			console.log("adminMenu=", adminMenu);
		},



		/**
		 * When the screen width slides down into mobile width range, auto close the side menu.  
		 * When it slides back up into desktop width range, auto re-open the side menu.
		 */
		onWindowResize()
		{
			const mobileMaxWidth = 820;
			this.isMobile = window.innerWidth < mobileMaxWidth;

			// Mobile width
			if (this.isMobile)
			{
				// Menu close already triggered. Don't do it again.
				if (this.triggerClosedMenu) return;
				this.isSideNavClosed = true;	// Close the side meny.
				this.triggerClosedMenu = true;

				this.triggerOpenMenu = false;
				return;
			}

			// Desktop width
			else if (!this.isMobile)
			{
				this.triggerClosedMenu = false;
				this.isSideNavClosed = false;	// Re-open the side menu.
				if (this.triggerOpenMenu) return;
				this.triggerOpenMenu = true;
			}

		},

		

		canShowPageTitle()
		{
			const route = this.$route;
			let module = route.meta.module ? route.meta.module : "";
			//console.log("nav(): module=", module, " | route=", route);
			
			module = module.toLowerCase();

			if (module == "tesstaClients".toLowerCase()) return false;


			return true;
		},



		/**
		 * The display name for a `meta.module` code, from `config.modules`.
		 * Falls back to the capitalised code for modules without a config entry.
		 */
		getModuleDisplayName(moduleCode)
		{
			const module = config.modules[moduleCode];
			if (module && module.displayName) return module.displayName;

			return moduleCode.charAt(0).toUpperCase() + moduleCode.slice(1);
		},



		/**
		 * The label a route shows in the breadcrumb trail:
		 * `meta.breadcrumbLabel` when set, otherwise the last part of `meta.pageTitle`.
		 * `{placeholder}` tokens are resolved with the current page's values.
		 */
		getBreadcrumbLabel(meta)
		{
			if (meta.breadcrumbLabel) return resolvePageTitleText(meta.breadcrumbLabel);

			const pageTitle = meta.pageTitle ? meta.pageTitle : "";
			const parts = pageTitle.split(' > ')
				.map((part) => resolvePageTitleText(part))
				.filter((part) => part != "");

			return parts.length > 0 ? parts[parts.length - 1] : "";
		},



		/**
		 * The breadcrumb label for the page being viewed. Unlike the parent crumbs this
		 * goes through the page-title module, so a title set with setPageTitle() (rather
		 * than through `meta.pageTitle`) shows in the trail too.
		 */
		getCurrentBreadcrumbLabel()
		{
			if (this.$route.meta.breadcrumbLabel) return resolvePageTitleText(this.$route.meta.breadcrumbLabel);

			const parts = resolvePageTitleSegments(this.$route);

			return parts.length > 0 ? parts[parts.length - 1] : "";
		},



		/**
		 * Escape page data being placed into a v-html'd string (the header title).
		 */
		escapeHtml(text)
		{
			const element = document.createElement("div");
			element.textContent = text == null ? "" : String(text);

			return element.innerHTML;
		},



		getPathSeparator()
		{
			return '<span style="font-size: 60%; margin-left: 10px; margin-right: 10px;"><i class="fa-solid fa-chevron-right"></i></span>';
		},



		initForAccountAdmin()
		{
			this.accessAccountId = this.getCurrentAccountAdminAccountId();

			if (this.isAccountAdmin())
			{
				this.loadAccount();		// From: accont-admin-mixin.js
			}
		},

		

		isAccountAdmin()
		{
			//TODO: Do this properly, with a feature access check. But for the moment it's fine.
			//		(Note: the `layoutType == 'account-admin'` probably doesn't need to change in the HTML part of this file when we change this method)
			return this.layoutType == 'account-admin';
		},



		/**
		 * Authorise user access to this page(s) if trying to access /system-admin.
		 */
		authoriseAccessIfSystemAdmin()
		{
			var path = this.$root.getCurrentRoutePath();

			var isSystemAdminPage = path.startsWith("/system-admin/");

			if (isSystemAdminPage == false)
			{
				// This page is not for system admin, so skip this check and keep loading.
				return;
			}

			if (this.$root.isSystemAdminUser() == false)
			{
				// User cannot access system admin pages, so rediect to the standard home page.
				this.$root.redirectToHomePage();
				
				return;
			}
		},


		
		getContentContinerType()
		{
			if (this.layoutContainer == 'card') return "card";
			if (this.layoutContainer == 'none') return "none";

			return "card";
		},

		

		onTopNavBarToggleMenu()
		{
			if (this.isSideNavClosed)
			{
				this.isSideNavClosed = false;
			}
			else
			{
				this.isSideNavClosed = true;
			}
		
		},

		

		goBack()
		{
			history.back();
		},



		
		hasHistory()
		{
			console.log("hasHistory(): history", history);
			return history.length > 1;
		},



		currentUserInitials()
		{
			var first = "";
			if (this.globalStore.state.currentUser.firstName.length > 0)
			{
				first = this.globalStore.state.currentUser.firstName.substring(0, 1);
			}
			
			var last = "";
			if (this.globalStore.state.currentUser.lastName.length > 0)
			{
				last = this.globalStore.state.currentUser.lastName.substring(0, 1);
			}

			return first + "" + last;
		},



		currentUserDisplayName()
		{
			var first = "";
			if (this.globalStore.state.currentUser.firstName.length > 0)
			{
				first = this.globalStore.state.currentUser.firstName;
			}

			return first;	
			
			//TODO: Remove. What's the value in showing a user their last initial?
			// var last = "";
			// if (this.globalStore.state.currentUser.lastName.length > 0)
			// {
			// 	last = this.globalStore.state.currentUser.lastName.substring(0, 1);
			// }
			//
			// return first + " " + last;
			
		}

	}
}
</script>
